import React, { useState, useEffect, useCallback } from 'react';
import { FaChevronLeft, FaChevronRight, FaTimes, FaExpand } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

// Dynamically import all images from the gallery folder
const importAllImages = () => {
  const images = [];
  for (let i = 1; i <= 63; i++) {
    try {
      const image = require(`../assets/gallery/${i}.jpeg`);
      images.push({
        id: i,
        src: image.default || image,
        alt: `Ministry activity ${i}`,
        caption: `Ministry work - Image ${i}`
      });
    } catch (error) {
      console.warn(`Image ${i}.jpeg not found`);
    }
  }
  return images;
};

const ministryImages = importAllImages();

const ImageSliderGallery = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [viewMode, setViewMode] = useState<'grid' | 'slider'>('grid');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [loadedImages, setLoadedImages] = useState<number[]>([]);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  // Preload next few images for smoother transitions
  useEffect(() => {
    const preloadImages = [currentIndex, currentIndex + 1, currentIndex - 1]
      .filter(index => index >= 0 && index < ministryImages.length)
      .filter(index => !loadedImages.includes(index));

    preloadImages.forEach(index => {
      const img = new Image();
      img.src = ministryImages[index].src;
      img.onload = () => {
        setLoadedImages(prev => [...prev, index]);
      };
    });
  }, [currentIndex, loadedImages]);

  const handleImageLoad = (id: number) => {
    if (!loadedImages.includes(id)) {
      setLoadedImages(prev => [...prev, id]);
    }
  };

  const nextSlide = useCallback(() => {
    setCurrentIndex(prev => (prev === ministryImages.length - 1 ? 0 : prev + 1));
  }, []);

  const prevSlide = useCallback(() => {
    setCurrentIndex(prev => (prev === 0 ? ministryImages.length - 1 : prev - 1));
  }, []);

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setViewMode('slider');
  };

  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 100) {
      nextSlide();
    }

    if (touchStart - touchEnd < -100) {
      prevSlide();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (viewMode === 'slider') {
        if (e.key === 'ArrowRight') nextSlide();
        if (e.key === 'ArrowLeft') prevSlide();
        if (e.key === 'Escape') {
          setIsFullscreen(false);
          if (isFullscreen) return;
          setViewMode('grid');
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [viewMode, nextSlide, prevSlide, isFullscreen]);

  // Auto-advance in slider mode
  useEffect(() => {
    if (viewMode !== 'slider') return;

    const interval = setInterval(() => {
      nextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [viewMode, nextSlide]);

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Gallery Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-blue-900 mb-4 font-serif">Ministry Moments</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {viewMode === 'grid'
              ? 'Browse through our gallery of ministry activities'
              : `${currentIndex + 1} of ${ministryImages.length}`}
          </p>
        </motion.div>

        {viewMode === 'grid' ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {ministryImages.slice(0, 12).map((image, index) => (
              <motion.div
                key={image.id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true, margin: "100px" }}
                whileHover={{ scale: 1.02 }}
                className="relative group cursor-pointer rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-all duration-300"
                onClick={() => goToSlide(index)}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-64 object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                  onLoad={() => handleImageLoad(index)}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <p className="text-white font-medium truncate">{image.caption}</p>
                </div>
              </motion.div>
            ))}
            <div className="col-span-2 sm:col-span-3 md:col-span-3 lg:col-span-4 flex justify-center mt-6">
              <button
                onClick={() => setViewMode('slider')}
                className="py-3 px-8 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors duration-300"
              >
                View All {ministryImages.length} Images
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Slider Navigation */}
            <div className="flex justify-between items-center mb-6">
              <button
                onClick={() => setViewMode('grid')}
                className="text-blue-600 hover:text-blue-800 font-medium flex items-center"
              >
                <FaChevronLeft className="mr-2" /> Back to Grid
              </button>
              <span className="text-gray-600">
                {currentIndex + 1} / {ministryImages.length}
              </span>
            </div>

            {/* Main Slider */}
            <div className="relative overflow-hidden rounded-xl shadow-xl bg-black">
              <div
                className="relative h-96 sm:h-[500px] flex transition-transform duration-500 ease-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
              >
                {ministryImages.map((image, index) => (
                  <div
                    key={image.id}
                    className="w-full flex-shrink-0 relative"
                  >
                    <img
                      src={image.src}
                      alt={image.alt}
                      className={`w-full h-full object-contain ${loadedImages.includes(index) ? 'opacity-100' : 'opacity-0'} transition-opacity duration-300`}
                      loading={index <= currentIndex + 2 ? 'eager' : 'lazy'}
                      onLoad={() => handleImageLoad(index)}
                    />
                    {loadedImages.includes(index) && (
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 text-white">
                        <p>{image.caption}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  prevSlide();
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-md p-3 rounded-full text-white transition-colors z-10"
                aria-label="Previous image"
              >
                <FaChevronLeft className="text-2xl" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  nextSlide();
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-md p-3 rounded-full text-white transition-colors z-10"
                aria-label="Next image"
              >
                <FaChevronRight className="text-2xl" />
              </button>

              {/* Fullscreen Toggle */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFullscreen();
                }}
                className="absolute bottom-4 right-4 bg-white/20 hover:bg-white/30 backdrop-blur-md p-3 rounded-full text-white transition-colors z-10"
                aria-label={isFullscreen ? "Exit fullscreen" : "View fullscreen"}
              >
                <FaExpand className="text-xl" />
              </button>
            </div>

            {/* Thumbnail Navigation */}
            <div className="mt-6 flex overflow-x-auto pb-2 gap-2">
              {ministryImages.map((image, index) => (
                <button
                  key={image.id}
                  onClick={() => setCurrentIndex(index)}
                  className={`flex-shrink-0 w-16 h-16 rounded-md overflow-hidden border-2 transition-all ${currentIndex === index ? 'border-blue-500 scale-105' : 'border-transparent'}`}
                  aria-label={`Go to image ${index + 1}`}
                >
                  <img
                    src={image.src}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </button>
              ))}
            </div>
          </>
        )}

        {/* Fullscreen View */}
        <AnimatePresence>
          {isFullscreen && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 bg-black flex items-center justify-center p-4"
              onClick={toggleFullscreen}
            >
              <div className="relative w-full h-full">
                <button
                  onClick={toggleFullscreen}
                  className="absolute top-4 right-4 text-white hover:text-blue-400 transition-colors z-10"
                  aria-label="Close fullscreen"
                >
                  <FaTimes className="text-3xl" />
                </button>

                <div className="h-full w-full flex items-center justify-center">
                  <img
                    src={ministryImages[currentIndex].src}
                    alt={ministryImages[currentIndex].alt}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>

                <div className="absolute bottom-4 left-0 right-0 text-center text-white">
                  <p>{ministryImages[currentIndex].caption}</p>
                  <p className="text-sm opacity-80">
                    {currentIndex + 1} of {ministryImages.length}
                  </p>
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    prevSlide();
                  }}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-md p-3 rounded-full text-white transition-colors z-10"
                  aria-label="Previous image"
                >
                  <FaChevronLeft className="text-2xl" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    nextSlide();
                  }}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-md p-3 rounded-full text-white transition-colors z-10"
                  aria-label="Next image"
                >
                  <FaChevronRight className="text-2xl" />
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default ImageSliderGallery;